import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Select, MenuItem, Typography, IconButton, Stack, Paper } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { SuccessSnackbar } from "./Snackbar/SuccessSnackbar";
import { ErrorSnackbar } from "./Snackbar/ErrorSnackbar";
import { useQuestionnaireBuilderStore } from "../store/useQuestionnaireBuilderStore";

const QuestionnaireQuestionBuilder = ({ onQuestionnaireGenerated, setJsonError, jsonError }) => {
  const [jsonText, setJsonText] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState({
    type: "text",
    question: "",
    options: [],
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    questionnaireData: { external_id = "", title = "", scope = "", instructions = "" } = {},
    questions = [],
    addQuestion,
    removeQuestion,
    setQuestionnaireData,
    setQuestions,
  } = useQuestionnaireBuilderStore();

  const typeLabels = {
    text: "Pregunta abierta",
    "multiple-choice": "Opción múltiple",
  };

  const generateQuestionnaireJson = () => {
    const formattedElements = questions.map((questionItem, index) => {
      const base = {
        name: `${external_id}_${index + 1}`,
        title: questionItem.question,
        isRequired: true,
      };

      return questionItem.type === "multiple-choice"
        ? {
            ...base,
            type: "radiogroup",
            choices: questionItem.options.map((opt, i) => ({
              value: String.fromCharCode(65 + i),
              text: (opt || "").trim() || "nan",
            })),
          }
        : { ...base, type: "comment", maxLength: 500 };
    });

    return {
      questionnaire_external_id: external_id,
      title,
      scope,
      instructions,
      content: {
        locale: "es",
        pages: [
          {
            name: "page1",
            elements: formattedElements,
          },
        ],
        showProgressBar: "topbottom",
        progressBarType: "questions",
      },
    };
  };

  const handleAddOption = () => {
    setCurrentQuestion((prev) => ({ ...prev, options: [...prev.options, ""] }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...currentQuestion.options];
    newOptions[index] = value;
    setCurrentQuestion((prev) => ({ ...prev, options: newOptions }));
  };

  const handleAddQuestion = () => {
    const trimmedQuestion = currentQuestion.question.trim();
    const isValidMultipleChoice =
      currentQuestion.type !== "multiple-choice" || currentQuestion.options.some((optionItem) => optionItem.trim() !== "");
    const isValid = trimmedQuestion && isValidMultipleChoice;

    if (isValid) {
      const cleanedOptions = currentQuestion.options.filter((optionItem) => optionItem.trim() !== "");
      addQuestion({
        ...currentQuestion,
        question: trimmedQuestion,
        options: currentQuestion.type === "multiple-choice" ? cleanedOptions : [],
      });
      setCurrentQuestion({ type: "text", question: "", options: [] });
    }
  };

  const handleDeleteQuestion = (index) => {
    removeQuestion(index);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setShowSuccess(true);
    } catch (err) {
      setErrorMessage("Error al copiar el JSON al portapapeles.");
      setShowError(true);
    }
  };

  const handleJsonInputChange = (value) => {
    setJsonText(value);
    try {
      const parsed = JSON.parse(value);
      setJsonError?.(null);

      const extId = parsed.questionnaire_external_id || "";

      const elements = parsed.content?.pages?.[0]?.elements;

      if (extId && Array.isArray(elements)) {
        setQuestionnaireData({
          ...useQuestionnaireBuilderStore.getState().questionnaireData,
          external_id: extId,
          title: parsed.title || "",
          scope: parsed.scope || "",
          instructions: parsed.instructions || "",
          questionnaire_json: parsed,
        });

        const mappedQuestions = elements.map((el) => ({
          type: el.type === "radiogroup" ? "multiple-choice" : "text",
          question: el.title,
          options: el.choices?.map((choice) => choice.text) || [],
        }));
        setQuestions(mappedQuestions);
      }
    } catch (err) {
      setJsonError?.("El JSON no es válido. Verifique la sintaxis.");
    }
  };

  useEffect(() => {
    const json = generateQuestionnaireJson();
    const stringified = JSON.stringify(json, null, 2);
    setJsonText(stringified);
    onQuestionnaireGenerated?.(json);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [external_id, title, scope, instructions, questions]);

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: "1.8rem", mb: 4 }}>Crear preguntas</Typography>

      <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
        <Stack spacing={2}>
          <TextField
            label="Pregunta"
            fullWidth
            value={currentQuestion.question}
            onChange={(e) => setCurrentQuestion({ ...currentQuestion, question: e.target.value })}
          />

          <Select
            value={currentQuestion.type}
            onChange={(e) =>
              setCurrentQuestion({
                ...currentQuestion,
                type: e.target.value,
                options: [],
              })
            }
            fullWidth
          >
            <MenuItem value="text">Pregunta abierta</MenuItem>
            <MenuItem value="multiple-choice">Opción múltiple</MenuItem>
          </Select>

          {currentQuestion.type === "multiple-choice" &&
            currentQuestion.options.map((opt, i) => (
              <TextField key={i} label={`Opción ${i + 1}`} value={opt} onChange={(e) => handleOptionChange(i, e.target.value)} fullWidth />
            ))}

          {currentQuestion.type === "multiple-choice" && (
            <Button onClick={handleAddOption} startIcon={<AddIcon />} variant="outlined" disabled={currentQuestion.options.length >= 6}>
              Agregar opción
            </Button>
          )}

          <Button onClick={handleAddQuestion} variant="contained">
            Agregar pregunta
          </Button>
        </Stack>
      </Paper>

      <Typography sx={{ fontWeight: 600, fontSize: "1.2rem", mb: 3, mt: 6 }}>Preguntas creadas</Typography>

      {questions.map((q, i) => (
        <Paper key={i} sx={{ p: 2, mb: 2 }} elevation={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1">
                {i + 1}. {q.question} ({typeLabels[q.type] || q.type})
              </Typography>
              {q.type === "multiple-choice" &&
                q.options.map((opt, j) => (
                  <Typography variant="body2" key={j}>
                    - {opt}
                  </Typography>
                ))}
            </Box>

            <IconButton onClick={() => handleDeleteQuestion(i)} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        </Paper>
      ))}

      <Box mt={6} mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontWeight: 600, fontSize: "1.2rem" }}>JSON del cuestionario</Typography>
        <Button onClick={handleCopy} variant="outlined" startIcon={<ContentCopyIcon />}>
          Copiar JSON
        </Button>
      </Box>

      <TextField
        multiline
        fullWidth
        minRows={10}
        value={jsonText}
        error={!!jsonError}
        helperText={jsonError || "Pegue o edite el JSON aquí"}
        onChange={(e) => handleJsonInputChange(e.target.value)}
      />

      <SuccessSnackbar
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Copiado al portapapeles"
        message="El contenido se ha copiado exitosamente."
      />
      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Error al copiar" message={errorMessage} />
    </Box>
  );
};

export default QuestionnaireQuestionBuilder;
